/**
 * 交通IC・共用カード — ローカル Zip 正本（サーバー API なし）
 */
import * as XLSX from 'xlsx';
import { TppLocalError, tppLocalStore } from './systems/transit-pass-pool/tppLocalStore';
import {
  createNewTppZip,
  getLastTppZipFileName,
  getTppZipConnection,
  initTppZipAutoSync,
  isTppFsaaSupported,
  isTppZipConnected,
  openExistingTppZip,
  peekStoredTppZipResume,
  resumeLastTppZip,
  subscribeTppZipConnection,
  switchTppZipFile,
  disconnectTppZipSession,
  teardownTppZipAutoSync,
  tryRestoreTppZipSession,
  checkTppZipExternalChange,
  downloadCurrentTppZip,
} from './systems/transit-pass-pool/tppZipFile';
import i18n from '../i18n/config';

export {
  createNewTppZip,
  openExistingTppZip,
  resumeLastTppZip,
  tryRestoreTppZipSession,
  peekStoredTppZipResume,
  switchTppZipFile,
  disconnectTppZipSession,
  initTppZipAutoSync,
  teardownTppZipAutoSync,
  isTppZipConnected,
  isTppFsaaSupported,
  getLastTppZipFileName,
  getTppZipConnection,
  subscribeTppZipConnection,
  checkTppZipExternalChange,
  downloadCurrentTppZip,
};

export type { TppZipConnection } from './systems/transit-pass-pool/tppZipFile';

export interface TppAccessSummary {
  totalPoolCards: number;
  poolKey: string;
}

export interface TppPoolSettingsDto {
  poolKey: string;
  displayPrefix: string;
  displaySuffix: string;
  isoCurrencyCode: string | null;
  fractionDigits: number;
  inputRoundingMode: string;
  displayRoundingMode: string;
}

export interface TppCardSummary {
  id: number;
  poolKey: string;
  displayNumber: number;
  physicalId: string;
  isActive: boolean;
  balance: string;
  balanceNumber: number;
  isCheckedOut: boolean;
  openEventId: number | null;
  openEventRoute: string | null;
}

export interface TppCardCreated {
  id: number;
  poolKey: string;
  displayNumber: number;
  physicalId: string;
  isActive: boolean;
}

export function tppCardCreatedToSummary(card: TppCardCreated): TppCardSummary {
  return {
    id: card.id,
    poolKey: card.poolKey,
    displayNumber: card.displayNumber,
    physicalId: card.physicalId,
    isActive: card.isActive,
    balance: '0',
    balanceNumber: 0,
    isCheckedOut: false,
    openEventId: null,
    openEventRoute: null,
  };
}

export interface TppDashboardStats {
  totalCards: number;
  checkedOutCards: number;
  /** 有効・未貸出・残高あり（いま貸出できる枚数） */
  availableCards: number;
  /** 有効・未貸出・残高なし（先にチャージが必要） */
  needsChargeCards: number;
}

export type TppCardImportResult = {
  created: number;
  updated: number;
  errors: Array<{ row: number; message: string; code?: string }>;
};

export type TppCardsPage = {
  items: TppCardSummary[];
  total: number;
  page: number;
  pageSize: number;
};

export type TppEventSortField =
  | 'id'
  | 'eventKind'
  | 'outDate'
  | 'inDate'
  | 'borrowerName'
  | 'purpose'
  | 'route'
  | 'amount'
  | 'checkoutStatus';

export interface TppEventSummary {
  id: number;
  cardId: number;
  eventKind: string;
  outDate: string;
  inDate: string | null;
  departmentLabel?: string;
  borrowerName: string;
  purpose: string;
  place: string;
  route?: string;
  hasProof?: boolean;
  amountNumber: number | null;
  checkoutStatus: number;
}

export interface TppEventsPage {
  items: TppEventSummary[];
  total: number;
  page: number;
  pageSize: number;
}

function wrapError(e: unknown): never {
  if (e instanceof TppLocalError) {
    const err = new Error(e.code) as Error & {
      response?: { status?: number; data?: { error?: { code?: string; message?: string } } };
    };
    err.response = {
      status: e.statusCode,
      data: {
        error: {
          code: `tpp.errors.${e.code}`,
          message: i18n.t(`tpp.errors.${e.code}`, { defaultValue: e.message }),
        },
      },
    };
    throw err;
  }
  if (e instanceof Error && e.message === 'tpp_zip_not_loaded') {
    const err = new Error(e.message) as Error & {
      response?: { status?: number; data?: { error?: { code?: string; message?: string } } };
    };
    err.response = {
      status: 400,
      data: {
        error: {
          code: 'tpp.errors.zip_not_loaded',
          message: i18n.t('tpp.errors.zip_not_loaded', {
            defaultValue: '先に Zip ファイルを開くか新規作成してください。',
          }),
        },
      },
    };
    throw err;
  }
  throw e;
}

export function getTppLocalApiError(e: unknown): string {
  if (e instanceof TppLocalError) {
    return i18n.t(`tpp.errors.${e.code}`, { defaultValue: e.message });
  }
  if (e instanceof Error) {
    let code = e.message;
    if (/user gesture/i.test(code) || /show a file picker/i.test(code)) {
      code = 'zip_pick_requires_gesture';
    }
    if (code === 'zip_pick_cancelled') return '';
    if (code === 'zip_pick_requires_gesture') {
      return i18n.t('tpp.errors.zip_pick_requires_gesture', {
        defaultValue:
          'ファイル選択を開けませんでした。ページ上のボタンをもう一度クリックしてください。',
      });
    }
    if (code === 'zip_no_stored_handle') {
      return i18n.t('tpp.errors.zip_no_stored_handle', {
        defaultValue: '前回の Zip 参照がありません。「既存を開く」から選び直してください。',
      });
    }
    if (code === 'tpp_zip_not_loaded' || code === 'zip_invalid_bundle') {
      return i18n.t(`tpp.errors.${code === 'zip_invalid_bundle' ? 'zip_invalid' : 'zip_not_loaded'}`, {
        defaultValue: e.message,
      });
    }
  }
  return i18n.t('tpp.action_failed', { defaultValue: '操作に失敗しました。' });
}

export async function fetchTppAccessSummary(): Promise<TppAccessSummary> {
  try {
    if (!isTppZipConnected()) {
      return {
        totalPoolCards: 0,
        poolKey: 'default',
      };
    }
    return await tppLocalStore.accessSummary();
  } catch (e) {
    wrapError(e);
  }
}

export async function fetchTppDashboardStats(
  poolKey = 'default',
  options?: { includeInactive?: boolean }
): Promise<TppDashboardStats> {
  try {
    return await tppLocalStore.getDashboardStats(poolKey, {
      activeOnly: !options?.includeInactive,
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function fetchTppPoolSettings(poolKey = 'default'): Promise<TppPoolSettingsDto> {
  try {
    return await tppLocalStore.getPoolSettings(poolKey);
  } catch (e) {
    wrapError(e);
  }
}

export async function patchTppPoolSettings(
  poolKey: string,
  body: Partial<
    Pick<
      TppPoolSettingsDto,
      | 'displayPrefix'
      | 'displaySuffix'
      | 'isoCurrencyCode'
      | 'fractionDigits'
      | 'inputRoundingMode'
      | 'displayRoundingMode'
    >
  >
): Promise<TppPoolSettingsDto> {
  try {
    return await tppLocalStore.updatePoolSettings(poolKey, body);
  } catch (e) {
    wrapError(e);
  }
}

function parseCardImportSheet(file: ArrayBuffer): {
  rows: Array<{ poolKey?: string; displayNumber: number; physicalId: string; isActive?: boolean }>;
  errors: Array<{ row: number; message: string; code?: string }>;
} {
  const wb = XLSX.read(file, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return { rows: [], errors: [{ row: 0, message: 'no_sheet', code: 'no_sheet' }] };
  const aoa = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(sheet, {
    header: 1,
    defval: '',
  }) as (string | number | boolean)[][];
  if (aoa.length < 2) return { rows: [], errors: [{ row: 0, message: 'no_header', code: 'no_header' }] };

  let headerRowIdx = 0;
  for (let i = 0; i < Math.min(3, aoa.length); i++) {
    const cells = aoa[i].map((c) => String(c).toLowerCase());
    if (cells.some((c) => c.includes('display_number') || c.includes('physical_id'))) {
      headerRowIdx = i;
      break;
    }
  }
  const headers = aoa[headerRowIdx].map((h) =>
    String(h)
      .trim()
      .toLowerCase()
      .replace(/[\s\-']+/g, '_')
  );
  const idx = (name: string) => headers.findIndex((h) => h === name || h.includes(name));
  const dnIdx = idx('display_number') >= 0 ? idx('display_number') : 0;
  const phIdx = idx('physical_id') >= 0 ? idx('physical_id') : 1;
  const pkIdx = idx('pool_key');
  const actIdx = idx('is_active');

  const rows: Array<{ poolKey?: string; displayNumber: number; physicalId: string; isActive?: boolean }> = [];
  const errors: Array<{ row: number; message: string; code?: string }> = [];
  for (let r = headerRowIdx + 1; r < aoa.length; r++) {
    const line = aoa[r];
    if (!line || line.every((c) => String(c).trim() === '')) continue;
    const dn = Number(String(line[dnIdx] ?? '').replace(/,/g, ''));
    const physicalId = String(line[phIdx] ?? '').trim();
    if (!Number.isFinite(dn) || !physicalId) {
      errors.push({ row: r + 1, message: 'missing_required', code: 'missing_required' });
      continue;
    }
    const poolKey = pkIdx >= 0 ? String(line[pkIdx] ?? '').trim() || undefined : undefined;
    let isActive: boolean | undefined;
    if (actIdx >= 0) {
      const raw = String(line[actIdx] ?? '').trim().toLowerCase();
      if (raw === '' || raw === 'true' || raw === '1') isActive = true;
      else if (raw === 'false' || raw === '0') isActive = false;
    }
    rows.push({ displayNumber: dn, physicalId, poolKey, isActive });
  }
  return { rows, errors };
}

export async function importTppCardsExcel(file: File): Promise<TppCardImportResult> {
  try {
    const { rows, errors } = parseCardImportSheet(await file.arrayBuffer());
    if (errors.length > 0 && rows.length === 0) {
      const err = new Error('excel_validation') as Error & {
        response?: { status: number; data: { data: TppCardImportResult; error: { code: string } } };
      };
      err.response = {
        status: 400,
        data: {
          data: { created: 0, updated: 0, errors },
          error: { code: 'tpp.errors.excel_validation' },
        },
      };
      throw err;
    }
    const result = await tppLocalStore.importCardsFromRows(rows);
    return { ...result, errors };
  } catch (e) {
    wrapError(e);
  }
}

export async function fetchTppCards(
  poolKey = 'default',
  options?: {
    includeInactive?: boolean;
    page?: number;
    pageSize?: number;
    search?: string;
    checkedOutOnly?: boolean;
  }
): Promise<TppCardsPage> {
  try {
    return await tppLocalStore.listCardsPage(poolKey, {
      activeOnly: !options?.includeInactive,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 50,
      search: options?.search,
      checkedOutOnly: options?.checkedOutOnly,
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function fetchTppEventsPage(
  cardId: number,
  options?: {
    poolKey?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    sortBy?: TppEventSortField;
    sortDir?: 'asc' | 'desc';
  }
): Promise<TppEventsPage> {
  try {
    return await tppLocalStore.listEventsPage(cardId, options?.poolKey ?? 'default', {
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 100,
      search: options?.search,
      sortBy: options?.sortBy ?? 'outDate',
      sortDir: options?.sortDir ?? 'desc',
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function checkoutTpp(
  cardId: number,
  body: {
    poolKey?: string;
    outDate: string;
    purpose: string;
    place: string;
    route: string;
    departmentLabel?: string;
  }
): Promise<unknown> {
  try {
    return await tppLocalStore.checkout(cardId, body.poolKey ?? 'default', body);
  } catch (e) {
    wrapError(e);
  }
}

export async function checkinTpp(
  eventId: number,
  body: { inDate: string; amount: string; route: string; proof?: File | null }
): Promise<unknown> {
  try {
    const amount = Number(String(body.amount).replace(/,/g, '').trim());
    if (!Number.isFinite(amount)) {
      throw new TppLocalError('invalid_amount', 400);
    }
    return await tppLocalStore.checkin(eventId, {
      inDate: body.inDate,
      amount,
      route: body.route,
      proofFile: body.proof ?? undefined,
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function chargeTpp(
  cardId: number,
  body: { poolKey?: string; outDate: string; amount: string; note?: string }
): Promise<unknown> {
  try {
    return await tppLocalStore.charge(cardId, body.poolKey ?? 'default', {
      outDate: body.outDate,
      amount: (() => {
        const n = Number(String(body.amount).replace(/,/g, '').trim());
        if (!Number.isFinite(n)) {
          throw new TppLocalError('invalid_amount', 400);
        }
        // UI は正数入力。保存時は負数（チャージ）へ正規化。
        return n > 0 ? -n : n;
      })(),
      note: body.note,
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function createTppCard(body: {
  poolKey?: string;
  displayNumber: number;
  physicalId: string;
}): Promise<TppCardCreated> {
  try {
    return await tppLocalStore.createCard({
      poolKey: body.poolKey ?? 'default',
      displayNumber: body.displayNumber,
      physicalId: body.physicalId,
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function patchTppCard(
  cardId: number,
  body: { physicalId?: string; isActive?: boolean }
): Promise<unknown> {
  try {
    return await tppLocalStore.updateCard(cardId, body);
  } catch (e) {
    wrapError(e);
  }
}

export async function patchTppEvent(eventId: number, body: Record<string, unknown>): Promise<unknown> {
  try {
    let amount: number | null | undefined;
    if (body.amount === undefined) {
      amount = undefined;
    } else if (body.amount == null || body.amount === '') {
      amount = null;
    } else {
      const n = Number(String(body.amount).replace(/,/g, '').trim());
      if (!Number.isFinite(n)) {
        throw new TppLocalError('invalid_amount', 400);
      }
      amount = n;
    }
    return await tppLocalStore.updateEvent(eventId, {
      outDate: body.outDate as string | undefined,
      inDate: body.inDate as string | null | undefined,
      departmentLabel: body.departmentLabel as string | undefined,
      borrowerName: body.borrowerName as string | undefined,
      purpose: body.purpose as string | undefined,
      place: body.place as string | undefined,
      route: body.route as string | undefined,
      amount,
      checkoutStatus: body.checkoutStatus as 0 | 1 | undefined,
      eventKind: body.eventKind as 'usage' | 'charge' | undefined,
    });
  } catch (e) {
    wrapError(e);
  }
}

export async function fetchTppEventProofBlob(eventId: number): Promise<Blob> {
  try {
    return await tppLocalStore.getEventProofBlob(eventId);
  } catch (e) {
    wrapError(e);
  }
}

export async function uploadTppEventProof(eventId: number, file: File): Promise<unknown> {
  try {
    return await tppLocalStore.uploadEventProof(eventId, file);
  } catch (e) {
    wrapError(e);
  }
}

export async function exportTppEvents(body: Record<string, unknown>): Promise<{ blob: Blob; filename: string }> {
  try {
    const blob = await tppLocalStore.buildExportWorkbookBlob({
      poolKey: typeof body.poolKey === 'string' ? body.poolKey : 'default',
      cardId: typeof body.cardId === 'number' ? body.cardId : undefined,
      dateFrom: typeof body.dateFrom === 'string' ? body.dateFrom : undefined,
      dateTo: typeof body.dateTo === 'string' ? body.dateTo : undefined,
    });
    const day = new Date().toISOString().slice(0, 10);
    return { blob, filename: `transit-pass-pool_export_${day}.xlsx` };
  } catch (e) {
    wrapError(e);
  }
}
